import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
  useContext,
  FC
} from 'react';

import Box from '@rexlabs/box';
import { styled, StyleSheet, Styles } from '@rexlabs/styling';
import { withWhereabouts } from '@rexlabs/react-whereabouts';
import config from 'config';

import { COLORS } from 'src/features/reports/theme';
import ROUTES, { ROUTE_NAMES } from 'src/features/reports/routes';
import Sidebar from 'src/features/reports/components/sidebar/sidebar';
import { ReportTitle } from '../components/report-title';
import { ReportSettingsContext } from 'src/features/reports/providers/report-settings-provider';
import { api } from 'utils/api-client';
import useRouteConfig from 'src/features/reports/hooks/use-route-config';

import { compose } from 'utils/compose';

import { ReportQueryParams } from 'types/params';
import { Whereabouts } from 'types/common';

const OPEN_SIDEBAR_WIDTH = 245;
const CLOSED_SIDEBAR_WIDTH = 60;
const MAX_WIDTH = 1400;

const defaultStyles = StyleSheet({
  container: {
    height: '100%',
    position: 'relative',
    width: '100%'
  },
  sidebar: {
    transition: 'width 0.3s',
    height: '100%',
    position: 'fixed',
    backgroundColor: COLORS.BLUE.DARK,
    zIndex: 1
  },
  main: { zIndex: 0, width: '100%', minHeight: '100vh' },
  content: {
    width: '100%',
    maxWidth: MAX_WIDTH,
    paddingLeft: 25,
    paddingRight: 25
  }
});

interface SidebarCoreProps {
  whereabouts: Whereabouts<ReportQueryParams>;
  styles: Styles;
}

const MAP_ROUTE_PATH = ROUTES.APP.REPORT_MAP.config.path;
const MAP_ORIGIN = config.MAP_URL ? new URL(config.MAP_URL).origin : '';

interface PostMessageResponse {
  subRegion: {
    id: number;
    properties: {
      name: string;
      parent: string;
      status: string;
    };
  };
}

const SideBar: FC<SidebarCoreProps> = ({
  styles: s,
  whereabouts,
  children
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    window.innerWidth > MAX_WIDTH
  );
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [pendingSubRegionName, setPendingSubRegionName] = useState<
    string | null
  >(null);

  const { updateParams } = useRouteConfig<ReportQueryParams>({ whereabouts });

  const {
    standardSubRegions,
    premiumSubRegions,
    setSelectedProjectsInSubRegion,
    setSelectedReport,
    setSubRegionWithAllProjectsSelected
  } = useContext(ReportSettingsContext);

  const sidebarWidth = useMemo(
    () => (isSidebarOpen ? OPEN_SIDEBAR_WIDTH : CLOSED_SIDEBAR_WIDTH),
    [isSidebarOpen]
  );

  const onToggle = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const isMapRoute = whereabouts?.path === MAP_ROUTE_PATH;

  const regionProjects = useMemo(() => {
    return [...standardSubRegions, ...premiumSubRegions]
      .filter((region) => region.projects.length > 0)
      .map((region) => ({
        name: region.name,
        key: region.id,
        projects: region.projects.map((p) => ({ name: p.title, key: p.id }))
      }));
  }, [standardSubRegions, premiumSubRegions]);

  const handleMessage = useCallback(
    async ({ origin, data }: MessageEvent) => {
      try {
        if (origin !== MAP_ORIGIN) return;

        if (typeof data === 'object' && data?.type === 'MAP_READY') {
          const { data: tokenData } = await api.get('/map/token');
          iframeRef.current?.contentWindow?.postMessage(
            { type: 'AUTH_TOKEN', token: tokenData.access_token },
            MAP_ORIGIN
          );
          return;
        }

        if (typeof data === 'string') {
          if (!data) return;
          const parsedData = JSON.parse(data) as PostMessageResponse;
          if (!parsedData?.subRegion) return;

          const subRegionName = parsedData.subRegion.properties.name;

          if (regionProjects.length === 0) {
            setPendingSubRegionName(subRegionName);
            return;
          }

          const subRegion = regionProjects.find(
            (r) => r.name === subRegionName
          );

          if (subRegion) {
            setSelectedProjectsInSubRegion({
              [subRegion.key]: subRegion.projects.map((p) => p.key)
            });
            setSubRegionWithAllProjectsSelected([subRegion.key]);
            updateParams({ saved: undefined });
            setSelectedReport(null);
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Map message error:', e);
      }
    },
    [
      regionProjects,
      setSelectedProjectsInSubRegion,
      setSubRegionWithAllProjectsSelected,
      setSelectedReport,
      updateParams
    ]
  );

  // Process pending subRegion selection once regions load
  useEffect(() => {
    if (!pendingSubRegionName || regionProjects.length === 0) return;

    const subRegion = regionProjects.find(
      (r) => r.name === pendingSubRegionName
    );

    if (subRegion) {
      setSelectedProjectsInSubRegion({
        [subRegion.key]: subRegion.projects.map((p) => p.key)
      });
      setSubRegionWithAllProjectsSelected([subRegion.key]);
      updateParams({ saved: undefined });
      setSelectedReport(null);
    }

    setPendingSubRegionName(null);
  }, [
    pendingSubRegionName,
    regionProjects,
    setSelectedProjectsInSubRegion,
    setSubRegionWithAllProjectsSelected,
    updateParams,
    setSelectedReport
  ]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  return (
    <Box flexDirection="row" {...s('container')}>
      <Box width={sidebarWidth} {...s('sidebar')}>
        <Sidebar isSidebarOpen={isSidebarOpen} onToggle={onToggle} />
      </Box>
      {/* Persistent map iframe — always mounted, visibility toggled */}
      {config.MAP_URL && (
        <Box
          paddingLeft={sidebarWidth}
          style={{
            zIndex: isMapRoute ? 0 : -1,
            width: '100%',
            height: '100vh',
            overflow: 'hidden',
            position: isMapRoute ? 'relative' : 'absolute',
            visibility: isMapRoute ? 'visible' : 'hidden',
            pointerEvents: isMapRoute ? 'auto' : 'none'
          }}
        >
          <Box style={{ width: '100%', height: '100vh' }}>
            <iframe
              ref={iframeRef}
              src={`${config.MAP_URL}/embed`}
              width="100%"
              height="100%"
              style={{ border: 'none', display: 'block' }}
            />
          </Box>
        </Box>
      )}
      {/* Regular content — hidden when map is active */}
      {!isMapRoute && (
        <Box {...s('main')} paddingLeft={sidebarWidth} justifyContent="center">
          <Box {...s('content')}>
            <ReportTitle text={ROUTE_NAMES[whereabouts.path]} />
            {children}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default compose(styled(defaultStyles), withWhereabouts)(SideBar);
